/**
 * Hook to check billing data completeness for subscription gating.
 * Prevents subscription page access until all required billing fields are filled.
 *
 * Two specialized hooks are provided:
 * - useOrgBillingCompleteness: for company users (reads organizationContext)
 * - useTalentBillingCompleteness: for talent users (fetches profile once)
 *
 * @userstory US-STRIPE-07, US-STRIPE-08
 */
import { useState, useEffect, useMemo } from 'react';
import { useOrganization } from '../contexts/organizationContext';
import profileService, { GetFreelanceProfile } from '../services/api/profileService';
import type { Organization } from '../type/organization-types';

/** Required billing fields for organizations. */
const ORG_BILLING_FIELDS: (keyof Organization)[] = [
	'legalName',
	'vatNumber',
	'fiscalCode',
	'italianSdiCode',
	'billingAddress',
	'billingCity',
	'billingProvince',
	'billingZipCode',
	'billingCountryCode',
	'billingEmail',
];

/** Required billing fields for talents. */
const TALENT_BILLING_FIELDS: (keyof GetFreelanceProfile)[] = [
	'legalName',
	'vatNumber',
	'fiscalCode',
	'billingFiscalCode',
	'businessType',
	'billingAddress',
	'billingCity',
	'billingProvince',
	'billingZipCode',
	'billingCountryCode',
	'billingEmail',
];

function isFieldFilled(value: unknown): boolean {
	if (value === null || value === undefined) return false;
	if (typeof value === 'string') return value.trim().length > 0;
	return true;
}

export function checkOrgBillingComplete(org: Organization | null | undefined): boolean {
	if (!org) return false;
	return ORG_BILLING_FIELDS.every((field) => isFieldFilled(org[field]));
}

export function checkTalentBillingComplete(
	profile: GetFreelanceProfile | null | undefined,
): boolean {
	if (!profile) return false;
	return TALENT_BILLING_FIELDS.every((field) => isFieldFilled(profile[field]));
}

/**
 * Check billing completeness for the selected organization.
 * Uses data already in organizationContext — no extra API call.
 * @userstory US-STRIPE-07
 */
export function useOrgBillingCompleteness(): boolean {
	const { selectedOrganization } = useOrganization();
	return useMemo(
		() => checkOrgBillingComplete(selectedOrganization),
		[selectedOrganization],
	);
}

/**
 * Check billing completeness for the current talent user.
 * Fetches the profile once on mount and listens for billing-data-updated events.
 * @param enabled Set to false to skip fetching (e.g. when not in settings area).
 * @userstory US-STRIPE-08
 */
export function useTalentBillingCompleteness(enabled = true): { isBillingComplete: boolean; isLoading: boolean } {
	const [talentProfile, setTalentProfile] = useState<GetFreelanceProfile | null>(null);
	const [isLoading, setIsLoading] = useState(enabled);

	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		setIsLoading(true);

		const fetchProfile = async () => {
			try {
				const profile = await profileService.getFreelanceProfile();
				if (!cancelled) setTalentProfile(profile);
			} catch {
				// If fetch fails, treat as incomplete
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		fetchProfile();

		// Listen for billing data updates (dispatched after save on BillingPage)
		const handleBillingUpdated = (e: Event) => {
			const detail = (e as CustomEvent).detail;
			if (detail?.profile) {
				setTalentProfile(detail.profile);
			} else {
				fetchProfile();
			}
		};
		window.addEventListener('billing-data-updated', handleBillingUpdated);

		return () => {
			cancelled = true;
			window.removeEventListener('billing-data-updated', handleBillingUpdated);
		};
	}, [enabled]);

	const isBillingComplete = useMemo(
		() => checkTalentBillingComplete(talentProfile),
		[talentProfile],
	);

	return { isBillingComplete, isLoading };
}
